<template>
  <RouterLink v-if="getUserData" class="post" :to="linkOnPost(post.id)">
    <div class="suggested-for-you-layout flex items-start justify-between gap-3">
      <!-- Left: Avatar + User Info -->
      <div class="left-section suggested-grid flex-1 min-w-0">
        <!-- Avatar -->
        <div class="avatar-cell flex-shrink-0">
          <BaseAvatar :storage-id="getUserData.avatarFile?.storageId" :verified="getUserData.verified" :href="userRoute"
            :isAdmin="getUserData.isAdmin" :rating="getUserData.rating" :isCurrentUser="false" />
        </div>

        <!-- Tier badge directly under avatar -->
        <!-- <div v-if="getUserData.rating" class="badge-cell">
          <div class="badge-aligner">
            <ReputationBadge :score="getUserData.rating" :showTierName="true" :isCurrentUser="false" :tierSet="tierSet"
              size="micro" class="tier-badge-under-avatar" />
          </div>
        </div> -->

        <!-- Name and username line -->
        <div class="name-cell author-name-line flex items-center gap-2 flex-wrap">
          <div class="author-name hover:underline overflow-hidden text-ellipsis cursor-pointer">
            {{ fullName }}
          </div>
          <div v-if="getUserData.username"
            class="author-username text-[0.8rem] text-[var(--text-02)] hover:underline cursor-pointer">
            @{{ getUserData.username }}
          </div>
        </div>

        <!-- Post content preview -->
        <div class="bio-cell post-content-section mt-1">
          <div class="post-preview text-text02 text-xs text-left">
            <span v-if="post.title" class="post-title font-semibold block">
              {{ coretedText(post.title) }}
            </span>
            <span v-if="post.text" class="post-text">
              {{ coretedText(post.text) }}
            </span>
            <span v-if="!post.title && !post.text" class="italic opacity-60">
              No content available
            </span>
          </div>
        </div>
      </div>

      <!-- Right: Post date -->
      <div class="right-section flex-shrink-0">
        <span class="post__date text-xs text-[var(--text-02)]">{{ formattedTime }}</span>
      </div>
    </div>
  </RouterLink>
</template>

<script setup>
import { abbreviateNumber } from "@/utils/utils.js";
import { computed } from "vue";
import { getRelativeTime } from "@/utils/relativeTime.js";
import { useStore } from "vuex";
import ReputationBadge from "@/components/ReputationBadge.vue";

const props = defineProps({
  post: {
    type: Object,
    default: null,
  },
  onCloseDropdown: {
    type: Function,
    default: null
  }
});

const store = useStore();
const getUserData = computed(() => props.post.authorUser || null);

// Tier set for reputation badges
const tierSet = computed(() => store.getters.getTierSet);

const fullName = computed(() => {
  let firstName = getUserData.value.firstName;
  let lastName = getUserData.value.lastName;
  let maxWidth = 12; // Increased for better display

  firstName =
    firstName?.length <= maxWidth
      ? firstName
      : firstName?.slice(0, maxWidth) + "..";
  lastName =
    lastName?.length <= maxWidth
      ? lastName
      : lastName?.slice(0, maxWidth) + "..";
  if (!getUserData.value.firstName) {
    return getUserData.value.username;
  } else {
    return firstName + " " + lastName;
  }
});

const userRoute = computed(() => {
  return !getUserData.value.username
    ? `/profile/${getUserData.value?.id}`
    : `/user/${getUserData.value?.username}`;
});

const linkOnPost = (id) => {
  return {
    name: props.post.type === "reply" ? "reply" : "post",
    params: { id },
  };
};

const coretedText = (text) => {
  if (!text) {
    return "";
  }
  const cleanText = text.replace(/<.*?>/g, " ").trim();
  return cleanText.slice(0, 80); // Shortened for compact display
};

const now = computed(() => store.getters.getCurrentTime);
const formattedTime = computed(() => {
  return getRelativeTime(props.post.publishedAt, now, true);
});
</script>

<style scoped lang="scss">
.post {
  padding: 12px 8px;
  display: block;
  transition: 0.2s ease-in;
  border-bottom: 1px solid var(--post-border);
  border-radius: 0;

  &:hover {
    background-color: var(--theme-wise-hover, rgba(0, 0, 0, 0.02));
  }
}

// Match SuggestedPost layout structure
.suggested-for-you-layout {
  width: 100%;
}

.left-section {
  display: grid;
  grid-template-columns: 40px 1fr;
  grid-template-rows: auto auto auto;
  grid-template-areas:
    "avatar name"
    "badge name"
    ". bio";
  gap: 2px 8px;
  align-items: start;
}

.avatar-cell {
  grid-area: avatar;
  display: flex;
  justify-content: center;
}

.badge-cell {
  grid-area: badge;
  display: flex;
  justify-content: center;

  .badge-aligner {
    display: flex;
    justify-content: center;
    width: 100%;
  }
}

.name-cell {
  grid-area: name;
  align-self: center;
}

.bio-cell {
  grid-area: bio;
  align-self: start;
}

.author-name-line {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.author-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--foreground-primary);
  text-decoration: none;
}

.author-username {
  font-size: 0.75rem;
  color: var(--text-02);
  text-decoration: none;
}

.post-content-section {
  margin-top: 4px;

  .post-preview {
    line-height: 1.3;
    color: var(--text-02);

    .post-title {
      font-weight: 600;
      color: var(--foreground-primary);
      margin-bottom: 2px;
      font-size: 0.8rem;
    }

    .post-text {
      color: var(--text-02);
      font-size: 0.8rem;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.right-section {
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.tier-badge-under-avatar {
  font-size: 0.65rem !important;
  padding: 1px 4px !important;
  min-width: auto !important;
}
</style>